import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";
import { useAuth } from "../context/AuthContext";
import { validateSignUpFields } from "../utils/validation";

function SignUp() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { signUp, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const loadCaptcha = useCallback(async () => {
    try {
      const res = await fetch("/api/contact/captcha");
      let data = {};
      try {
        data = await res.json();
      } catch {
        throw new Error("Invalid captcha response");
      }

      if (!res.ok || !data.question || !data.captchaToken) {
        throw new Error(data.error || "Failed to load captcha");
      }

      setCaptchaQuestion(data.question);
      setCaptchaToken(data.captchaToken);
      setCaptchaAnswer("");
    } catch {
      setCaptchaQuestion("");
      setCaptchaToken("");
      setError("Unable to load captcha. Ensure the backend is running, then click ↻ to retry.");
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const inbox = location.state?.inbox;
      navigate(inbox ? `/dashboard?inbox=${inbox}` : "/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const errors = validateSignUpFields({ name, email, password });
    setFieldErrors(errors);
    if (errors.name || errors.email || errors.password) {
      return;
    }

    if (!acceptedTerms) {
      setError("You must accept the Terms of Service and Privacy Policy to sign up.");
      return;
    }

    if (!captchaAnswer.trim()) {
      setError("Please solve the math captcha.");
      return;
    }

    setSubmitting(true);

    const result = await signUp({
      name,
      email,
      password,
      acceptedTerms,
      captchaToken,
      captchaAnswer: captchaAnswer.trim(),
    });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.error);
      await loadCaptcha();
      return;
    }

    const inbox = location.state?.inbox;
    navigate(inbox ? `/dashboard?inbox=${inbox}` : "/dashboard", { replace: true });
  };

  return (
    <Layout>
      <SeoHead
        title="Sign Up – Ghost Inbox Account"
        description="Create a free Ghost Inbox account and get your own secure temporary inbox for software testing, QA workflows, and privacy protection."
        path="/signup"
      />
      <div className="mx-auto flex max-w-md flex-1 flex-col justify-center px-6 py-16">
        <h1 className="text-2xl font-bold text-white">Create your account</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Free access to secure @ghostinbox.xyz temporary inboxes.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-400">Full name</label>
            <input
              type="text"
              value={name}
              maxLength={50}
              onChange={(e) => {
                setName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, name: "" }));
              }}
              required
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
            />
            {fieldErrors.name && (
              <p className="mt-1 text-xs text-red-400">{fieldErrors.name}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-400">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: "" }));
              }}
              required
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
            />
            {fieldErrors.email && (
              <p className="mt-1 text-xs text-red-400">{fieldErrors.email}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-400">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((prev) => ({ ...prev, password: "" }));
              }}
              required
              minLength={6}
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none"
            />
            {fieldErrors.password && (
              <p className="mt-1 text-xs text-red-400">{fieldErrors.password}</p>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-900/50 p-4">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              required
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-600 bg-zinc-950 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-zinc-950"
            />
            <span className="text-sm leading-relaxed text-zinc-300">
              I agree to the{" "}
              <Link to="/terms" target="_blank" className="text-emerald-400 hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" className="text-emerald-400 hover:underline">
                Privacy Policy
              </Link>
              . <span className="text-red-400">*</span>
            </span>
          </label>

          <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4">
            <label htmlFor="signup-captcha" className="mb-2 block text-xs font-medium text-zinc-400">
              Verification <span className="text-red-400">*</span>
            </label>
            <p className="text-sm text-zinc-300">
              What is{" "}
              <span className="font-mono text-base font-semibold text-emerald-400">
                {captchaQuestion || "…"}
              </span>
              ?
            </p>
            <div className="mt-3 flex gap-2">
              <input
                id="signup-captcha"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Your answer"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value.replace(/\D/g, ""))}
                required
                className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={loadCaptcha}
                className="shrink-0 rounded-lg border border-zinc-700 px-3 text-sm text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                title="New captcha"
                aria-label="Generate new captcha"
              >
                ↻
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={submitting || !captchaToken || !acceptedTerms}
            className="w-full rounded-lg bg-emerald-500 py-3 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Creating account…" : "Sign Up"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-zinc-500">
          Already have an account?{" "}
          <Link to="/login" className="text-emerald-400 hover:underline">
            Login
          </Link>
        </p>
      </div>
    </Layout>
  );
}

export default SignUp;
